import { ZodSchema } from 'zod/v3';
import { Ty } from '../ty';
import type { Def } from '../erod/erod-schema';
import type { JsonSchema } from '../erod/erod-to-json-schema';
import { extendedAttributeTypeJsonSchema } from '../erod/erod-to-json-schema';
import { ExtendedAttributeTypeSchema } from './base-zod';

/**
 * Erod definition of Ty.ExtendedAttributeType.
 * Lives side by side with the legacy Zod schema in parser/base-zod.js.
 *
 * This wraps the legacy Parser.ExtendedAttributeTypeSchema to provide
 * a Def interface, since the recursive structure (struct with recordWithKey)
 * is not compatible with erod's discriminatedUnion (which requires struct-only options).
 *
 * Use Erod.ExtendedAttributeTypeErodSchema for erod-based validation,
 * or Parser.ExtendedAttributeTypeSchema (from base) for the legacy Zod schema.
 */
class ExtendedAttributeTypeErodDef {
  readonly shape = { t: 'base' as const };
  readonly description: string | null = null;
  readonly isNullable = false;
  readonly defaultValue = { specified: false as const };

  toJsonSchema(opts?: {
    nullable?: boolean;
    provider?: import('../erod/erod-to-json-schema').LlmProvider;
  }): JsonSchema {
    return extendedAttributeTypeJsonSchema(
      opts?.nullable ?? false,
      opts?.provider ?? 'default'
    );
  }

  get ty(): Ty.ExtendedAttributeType {
    return {
      ty: { k: 'primitive', t: 'string' },
      nullable: false,
      tags: [],
    };
  }

  zodSchema(_opts?: { strict?: boolean }): ZodSchema<Ty.ExtendedAttributeType> {
    return ExtendedAttributeTypeSchema as ZodSchema<Ty.ExtendedAttributeType>;
  }

  prettyPrint(): string {
    return 'ExtendedAttributeType (wraps legacy Zod schema)';
  }

  get(_key: string): Def<Ty.ExtendedAttributeType> | null {
    return null;
  }

  describe(_description: string | null): Def<Ty.ExtendedAttributeType> {
    return this as Def<Ty.ExtendedAttributeType>;
  }

  nullable(): Def<Ty.ExtendedAttributeType | null> {
    return new NullableExtendedAttributeTypeErodDef(this);
  }

  default(_val: Ty.ExtendedAttributeType): Def<Ty.ExtendedAttributeType> {
    return this as Def<Ty.ExtendedAttributeType>;
  }

  array(): Def<Ty.ExtendedAttributeType[]> {
    throw new Error('ExtendedAttributeTypeErodDef.array() not implemented');
  }

  record(): Def<Record<string, Ty.ExtendedAttributeType>> {
    throw new Error('ExtendedAttributeTypeErodDef.record() not implemented');
  }

  extend(): never {
    throw new Error('ExtendedAttributeTypeErodDef.extend() not implemented');
  }
}

/**
 * Wrapper Def that adds nullable validation to ExtendedAttributeTypeErodDef.
 * Required because the base Def's zodSchema() returns the raw schema without
 * .nullable() wrapping; tool input validation would reject null otherwise.
 */
class NullableExtendedAttributeTypeErodDef
  implements Def<Ty.ExtendedAttributeType | null>
{
  readonly shape = { t: 'base' as const };
  readonly isNullable = true;
  readonly defaultValue = { specified: false as const };

  constructor(
    private readonly _inner: ExtendedAttributeTypeErodDef,
    readonly description: string | null = null
  ) {}

  get ty(): Ty.ExtendedAttributeType {
    return { ...this._inner.ty, nullable: true };
  }

  toJsonSchema(opts?: {
    nullable?: boolean;
    provider?: import('../erod/erod-to-json-schema').LlmProvider;
  }): JsonSchema {
    return extendedAttributeTypeJsonSchema(
      opts?.nullable ?? true,
      opts?.provider ?? 'default'
    );
  }

  zodSchema(opts?: {
    strict?: boolean;
  }): ZodSchema<Ty.ExtendedAttributeType | null> {
    const base = this._inner.zodSchema(opts);
    return (
      opts?.strict ? base.nullable() : base.nullish().default(null)
    ) as ZodSchema<Ty.ExtendedAttributeType | null>;
  }

  prettyPrint(): string {
    return this._inner.prettyPrint();
  }

  get(_key: string): Def<Ty.ExtendedAttributeType | null> | null {
    return null;
  }

  describe(description: string | null): Def<Ty.ExtendedAttributeType | null> {
    return new NullableExtendedAttributeTypeErodDef(this._inner, description);
  }

  nullable(): Def<Ty.ExtendedAttributeType | null> {
    return this;
  }

  default(
    _val: Ty.ExtendedAttributeType | null
  ): Def<Ty.ExtendedAttributeType | null> {
    return this as Def<Ty.ExtendedAttributeType | null>;
  }

  array(): Def<(Ty.ExtendedAttributeType | null)[]> {
    throw new Error('ExtendedAttributeTypeErodDef.array() not implemented');
  }

  record(): Def<Record<string, Ty.ExtendedAttributeType | null>> {
    throw new Error('ExtendedAttributeTypeErodDef.record() not implemented');
  }

  extend(): never {
    throw new Error('ExtendedAttributeTypeErodDef.extend() not implemented');
  }
}

export const ExtendedAttributeTypeErodSchema =
  new ExtendedAttributeTypeErodDef() as Def<Ty.ExtendedAttributeType>;

/**
 * Schema for outputShape that only exposes `null` to the LLM.
 * Produces minimal JSON schema ({ oneOf: [{ type: 'null' }] }) to avoid
 * overwhelming models with the full recursive ExtendedAttributeType schema.
 * Use for ad-hoc sub-agent tools where "pass null for freeform text" is the common case.
 * Validates as Ty.ExtendedAttributeType | null (accepts null; rejects invalid objects).
 */
class OutputShapeNullOnlyErodDef
  implements Def<Ty.ExtendedAttributeType | null>
{
  readonly shape = { t: 'base' as const };
  readonly isNullable = true;
  readonly defaultValue = { specified: false as const };

  constructor(readonly description: string | null = null) {}

  get ty(): Ty.ExtendedAttributeType {
    return { ty: { k: 'primitive', t: 'string' }, nullable: true, tags: [] };
  }

  toJsonSchema(): JsonSchema {
    return { oneOf: [{ type: 'null' }] };
  }

  zodSchema(opts?: {
    strict?: boolean;
  }): ZodSchema<Ty.ExtendedAttributeType | null> {
    const base =
      ExtendedAttributeTypeSchema as ZodSchema<Ty.ExtendedAttributeType>;
    return (
      opts?.strict ? base.nullable() : base.nullish().default(null)
    ) as ZodSchema<Ty.ExtendedAttributeType | null>;
  }

  prettyPrint(): string {
    return 'OutputShape (null only for LLM)';
  }

  get(_key: string): Def<Ty.ExtendedAttributeType | null> | null {
    return null;
  }

  describe(description: string | null): Def<Ty.ExtendedAttributeType | null> {
    return new OutputShapeNullOnlyErodDef(description);
  }

  nullable(): Def<Ty.ExtendedAttributeType | null> {
    return this;
  }

  default(
    _val: Ty.ExtendedAttributeType | null
  ): Def<Ty.ExtendedAttributeType | null> {
    return this;
  }

  array(): Def<(Ty.ExtendedAttributeType | null)[]> {
    throw new Error('OutputShapeNullOnlyErodDef.array() not implemented');
  }

  record(): Def<Record<string, Ty.ExtendedAttributeType | null>> {
    throw new Error('OutputShapeNullOnlyErodDef.record() not implemented');
  }

  extend(): never {
    throw new Error('OutputShapeNullOnlyErodDef.extend() not implemented');
  }
}

export const OutputShapeNullOnlyErodSchema =
  new OutputShapeNullOnlyErodDef() as Def<Ty.ExtendedAttributeType | null>;
